import { Tabs } from 'antd';
import { ShieldAlert } from 'lucide-react';
import { useNavigate } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { sectionTrail } from '../../../shared/components/section-trail';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { CloseOutTable } from '../components/CloseOutTable';
import { CLOSE_OUT_TABS, CLOSE_OUT_TAB_LABELS, closeOutTabPath, type CloseOutTab } from '../distance-agreement.paths';
import { useCloseOutTrips } from '../hooks/use-close-out-trips';

/** The close-out workspace: every closed-out trip with its odometer and GPS distances side by side, the open gaps first. */
export function CloseOutPage({ tab }: { tab: CloseOutTab }) {
  const navigate = useNavigate();
  const { state, list, isRefreshing } = useCloseOutTrips(tab);

  // The route's errorElement shows the failure, so the page does not invent a second kind of error screen.
  if (state.status === 'error') throw state.error;

  return (
    <>
      <PageHeader
        breadcrumbs={sectionTrail('close-out')}
        title="Close-out"
        description="Each closed-out trip's odometer distance against its GPS distance. Open a trip to see where each figure came from and to record how a gap was settled."
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(closeOutTabPath(key as CloseOutTab))}
        items={CLOSE_OUT_TABS.map((key) => ({ key, label: CLOSE_OUT_TAB_LABELS[key] }))}
      />
      {state.status === 'forbidden' && (
        <EmptyState
          icon={ShieldAlert}
          title="You can't see close-out"
          description="Comparing a trip's distances needs the permission to view them. Ask an administrator."
        />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading closed-out trips" withSearch rows={6} />}
      {state.status === 'loaded' && <CloseOutTable tab={tab} page={state.page} list={list} isRefreshing={isRefreshing} />}
    </>
  );
}
